<template>
  <div class="plane-mirror-imaging-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$L_object = L_image$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- 背景格点 -->
           <defs>
             <pattern id="grid-pm" width="25" height="25" patternUnits="userSpaceOnUse">
               <circle cx="1.5" cy="1.5" r="1" fill="rgba(255,255,255,0.06)" />
             </pattern>
           </defs>
           <rect width="100%" height="100%" fill="url(#grid-pm)" />

           <!-- 平面镜 (Vertical Mirror) -->
           <rect x="248" y="30" width="4" height="180" fill="#94a3b8" rx="2" />
           <line x1="250" y1="30" x2="250" y2="210" stroke="#f8fafc" stroke-width="1.5" opacity="0.8" />
           <!-- 镜面反面阴影 (Shadow to show direction) -->
           <line v-for="i in 18" :key="i"
                 :x1="250" :y1="30 + i*10" :x2="255" :y2="30 + i*10 - 5" 
                 stroke="rgba(148, 163, 184, 0.3)" stroke-width="1" />

           <!-- 地面/参考线 -->
           <line x1="50" y1="210" x2="450" y2="210" stroke="rgba(255,255,255,0.1)" stroke-width="1" stroke-dasharray="4,4" />

           <!-- 物体 (Object) -->
           <g :transform="`translate(${250 - distance}, ${210 - objHeight})`" class="object-item">
              <path d="M -8 0 Q 0 -40 8 0 Z" fill="#38bdf8" stroke="#0284c7" stroke-width="1.5" />
              <circle cx="0" cy="-40" r="5" fill="#fbbf24" stroke="#d97706" />
              <text y="15" text-anchor="middle" fill="#38bdf8" font-size="10" font-weight="700">物体 A</text>
           </g>

           <!-- 虚像 (Virtual Image) -->
           <g :transform="`translate(${250 + distance}, ${210 - objHeight})`" class="image-item" opacity="0.4">
              <path d="M -8 0 Q 0 -40 8 0 Z" fill="#38bdf8" stroke="#0284c7" stroke-width="1.5" stroke-dasharray="2,1" />
              <circle cx="0" cy="-40" r="5" fill="#fbbf24" stroke="#d97706" opacity="0.6" />
              <text y="15" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="700">虚像 A'</text>
           </g>

           <!-- 光路演示 (Rays) -->
           <g class="ray-system">
              <!-- 入射光线 1 (Top) -->
              <line :x1="250 - distance" :y1="210 - objHeight - 40" 
                    :x2="250" :y2="210 - objHeight - 40 + 40" 
                    stroke="#fbbf24" stroke-width="1" />
              <!-- 反射光线 1 -->
              <line x1="250" :y1="210 - objHeight - 40 + 40" 
                    :x2="450" :y2="210 - objHeight - 40 + 40 + (40/distance)*200" 
                    stroke="#10b981" stroke-width="1" />
              <!-- 虚像反向延长线 1 -->
              <line :x1="250 + distance" :y1="210 - objHeight - 40" 
                    x2="250" :y2="210 - objHeight - 40 + 40" 
                    stroke="#10b981" stroke-width="1" stroke-dasharray="4,2" opacity="0.6" />

              <!-- 入射光线 2 (Bottomish) -->
              <line :x1="250 - distance" :y1="210 - objHeight - 40" 
                    :x2="250" :y2="210 - objHeight - 40 - 40" 
                    stroke="#fbbf24" stroke-width="1" />
              <!-- 反射光线 2 -->
              <line x1="250" :y1="210 - objHeight - 40 - 40" 
                    :x2="450" :y2="210 - objHeight - 40 - 40 - (40/distance)*200" 
                    stroke="#10b981" stroke-width="1" />
              <!-- 虚像反向延长线 2 -->
              <line :x1="250 + distance" :y1="210 - objHeight - 40" 
                    x2="250" :y2="210 - objHeight - 40 - 40" 
                    stroke="#10b981" stroke-width="1" stroke-dasharray="4,2" opacity="0.6" />
           </g>

           <!-- 距离标注 -->
           <g class="dim-lines">
              <line :x1="250 - distance" y1="220" x2="250" y2="220" stroke="#64748b" stroke-width="1" marker-start="url(#arr-s)" marker-end="url(#arr-s)" />
              <text :x="250 - distance/2" y="232" text-anchor="middle" fill="#64748b" font-size="9">物距 S={{ distance.toFixed(0) }}</text>
              
              <line x1="250" y1="220" :x2="250 + distance" y2="220" stroke="#64748b" stroke-width="1" marker-start="url(#arr-s)" marker-end="url(#arr-s)" />
              <text :x="250 + distance/2" y="232" text-anchor="middle" fill="#64748b" font-size="9">像距 S'={{ distance.toFixed(0) }}</text>
           </g>

           <defs>
              <marker id="arr-s" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto"><path d="M 0 0 L 10 5 L 0 10 Z" fill="#64748b"/></marker>
           </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>像的性质</label>
              <span class="val">等大、正立、虚像</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">物距 (Distance)</label>
                 <el-slider v-model="distance" :min="30" :max="180" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">物体高度 (Height)</label>
                 <el-slider v-model="objHeight" :min="10" :max="120" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-picture-outline"></i>
           <p><strong>成像特点：</strong>像与物关于镜面对称。像距始终等于物距，像的大小始终等于物的大小。平面镜成的是<strong>虚像</strong>，由反射光线的反向延长线汇聚而成。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlaneMirrorImagingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      distance: 100,
      objHeight: 60
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '平面镜成像的特点与原理';
    }
  }
}
</script>

<style scoped lang="scss">
.plane-mirror-imaging-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #06b6d4; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 20px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
